import React, { useState } from 'react';
import { 
  FaMicrochip, 
  FaCamera, 
  FaBatteryFull, 
  FaBolt, 
  FaShieldAlt, 
  FaSun, 
  FaSlidersH,
  FaVolumeMute,
  FaLightbulb,
  FaVideo
} from 'react-icons/fa';
import useScrollAnimation from './useScrollAnimation';

const BentoGrid = () => {
  const [sectionRef, isVisible] = useScrollAnimation({ threshold: 0.1 });
  const [activeAction, setActiveAction] = useState('camera');
  const [isGpuBoosted, setIsGpuBoosted] = useState(false);

  const actionButtonOptions = [
    { id: 'silent', name: 'Silent Mode', icon: <FaVolumeMute /> },
    { id: 'camera', name: 'Camera', icon: <FaCamera /> },
    { id: 'flash', name: 'Flashlight', icon: <FaLightbulb /> },
    { id: 'video', name: 'Video Rec', icon: <FaVideo /> }
  ];

  return (
    <section 
      ref={sectionRef} 
      className={`bento-section ${isVisible ? 'is-visible' : ''}`}
      id="features"
    >
      <div className="section-container">
        {/* Section Header */}
        <div className="text-center bento-header scroll-fade-up">
          <span className="section-eyebrow">Pro Power Unleashed</span>
          <h2 className="section-headline">Engineered to change everything.</h2>
          <p className="section-subheadline">
            Explore the cutting-edge innovations packed into the titanium body of Peace i17 Air.
          </p>
        </div>

        {/* Bento Grid Architecture */}
        <div className="bento-grid">
          
          {/* Card 1: A17 Pro Silicon Monster (Large Card, Spans 2 Cols) */}
          <div className="bento-card bento-card-large bento-card-chip scroll-fade-up">
            <div className="bento-card-header">
              <div className="bento-icon-wrapper">
                <FaMicrochip className="bento-icon" />
              </div>
              <span className="bento-badge">Industry-First 3nm</span>
            </div>
            
            <div className="bento-card-body">
              <h3 className="bento-title">A17 Pro Bionic Chip</h3>
              <p className="bento-text">
                The biggest redesign in GPU history. Hardware-accelerated ray tracing delivers 4x faster graphics with unprecedented realism.
              </p>

              {/* Interactive Performance Meter */}
              <div className="chip-perf-widget">
                <div className="perf-metric-header">
                  <span>GPU Benchmark Speed</span>
                  <button 
                    className="boost-toggle-btn"
                    onClick={() => setIsGpuBoosted(!isGpuBoosted)}
                  >
                    {isGpuBoosted ? 'Boost: Active (60 FPS)' : 'Click to Benchmark'}
                  </button>
                </div>
                <div className="perf-bar-track">
                  <div 
                    className={`perf-bar-fill ${isGpuBoosted ? 'boosted' : ''}`} 
                    style={{ width: isGpuBoosted ? '96%' : '78%' }}
                  />
                </div>
                <div className="perf-stats-row">
                  <div><strong>6-Core</strong> GPU</div>
                  <div><strong>16-Core</strong> Neural Engine</div>
                  <div><strong>Up to 20%</strong> Faster CPU</div>
                </div>
              </div>
            </div>

            <div className="bento-glow-ambient chip-glow" />
          </div>

          {/* Card 2: Titanium Enclosure (Medium Card) */}
          <div className="bento-card bento-card-medium bento-card-titanium scroll-fade-up">
            <div className="bento-card-header">
              <div className="bento-icon-wrapper">
                <FaShieldAlt className="bento-icon" />
              </div>
              <span className="bento-badge">Aerospace Grade</span>
            </div>

            <div className="bento-card-body">
              <h3 className="bento-title">Titanium Frame</h3>
              <p className="bento-text">
                The strongest and lightest metal casing with precision contoured edges that feel incredible in your hand.
              </p>
              
              <div className="titanium-preview-box">
                <img 
                  src="/Photo/i7 air 2.jpg" 
                  alt="Titanium chassis" 
                  className="titanium-thumb"
                />
              </div>
            </div>

            <div className="bento-glow-ambient titanium-glow" />
          </div>

          {/* Card 3: 48MP Pro Photonic Camera (Medium Card) */}
          <div className="bento-card bento-card-medium bento-card-camera scroll-fade-up">
            <div className="bento-card-header">
              <div className="bento-icon-wrapper">
                <FaCamera className="bento-icon" />
              </div>
              <span className="bento-badge">Photonic Engine</span>
            </div>

            <div className="bento-card-body">
              <h3 className="bento-title">48MP Pro Camera</h3>
              <p className="bento-text">
                Equipped with 7 pro lenses in your pocket. Capture ultra-high-resolution 24MP and 48MP photos with rich depth and vivid HDR.
              </p>

              <div className="camera-focal-pills">
                <span className="focal-pill">0.5x Ultra Wide</span>
                <span className="focal-pill active">1x Main 24mm</span>
                <span className="focal-pill">2x Telephoto</span>
                <span className="focal-pill">5x Optical 120mm</span>
              </div>
            </div>

            <div className="bento-glow-ambient camera-glow" />
          </div>

          {/* Card 4: Action Button Tactile Feature (Medium Card) */}
          <div className="bento-card bento-card-medium bento-card-action scroll-fade-up">
            <div className="bento-card-header">
              <div className="bento-icon-wrapper">
                <FaSlidersH className="bento-icon" />
              </div>
              <span className="bento-badge">Custom Haptic</span>
            </div>

            <div className="bento-card-body">
              <h3 className="bento-title">Action Button</h3>
              <p className="bento-text">
                Press and hold to launch your selected shortcut. Experience instant physical feedback with dynamic island animations.
              </p>

              {/* Interactive Action Switcher */}
              <div className="action-button-switcher">
                {actionButtonOptions.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => setActiveAction(opt.id)}
                    className={`action-opt-btn ${activeAction === opt.id ? 'active' : ''}`}
                  >
                    <span className="action-opt-icon">{opt.icon}</span>
                    <span className="action-opt-label">{opt.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="bento-glow-ambient action-glow" />
          </div>

          {/* Card 5: All-Day Battery Life (Medium Card) */}
          <div className="bento-card bento-card-medium bento-card-battery scroll-fade-up">
            <div className="bento-card-header">
              <div className="bento-icon-wrapper">
                <FaBatteryFull className="bento-icon" />
              </div>
              <span className="bento-badge">29 Hrs Playback</span>
            </div>

            <div className="bento-card-body">
              <h3 className="bento-title">Monster Battery Life</h3>
              <p className="bento-text">
                Even with so many advanced new features, Peace i17 Air gives you all-day battery life that keeps going.
              </p>

              <div className="battery-gauge-box">
                <div className="battery-shell">
                  <div className="battery-fluid" />
                  <span className="battery-percentage">100%</span>
                </div>
                <div className="battery-spec-note">
                  <span>Up to <strong>29 hrs</strong> video playback</span>
                  <span><strong>95 hrs</strong> audio playback</span>
                </div>
              </div>
            </div>

            <div className="bento-glow-ambient battery-glow" />
          </div>

          {/* Card 6: Super Retina XDR with 120Hz ProMotion (Medium Card) */}
          <div className="bento-card bento-card-medium bento-card-display scroll-fade-up">
            <div className="bento-card-header">
              <div className="bento-icon-wrapper">
                <FaSun className="bento-icon" />
              </div>
              <span className="bento-badge">2000 Nits Peak</span>
            </div>

            <div className="bento-card-body">
              <h3 className="bento-title">Super Retina XDR</h3>
              <p className="bento-text">
                ProMotion automatically increases refresh rates to 120Hz for silky smooth scrolling and drops down to 1Hz for Always-On display.
              </p>

              <div className="display-features-row">
                <div className="display-pill">Always-On</div>
                <div className="display-pill">Dynamic Island</div>
                <div className="display-pill">True Tone HDR</div>
              </div>
            </div>

            <div className="bento-glow-ambient display-glow" />
          </div>

          {/* Card 7: USB-C 3.0 with 10Gbps (Small/Medium Card) */}
          <div className="bento-card bento-card-medium bento-card-usbc scroll-fade-up">
            <div className="bento-card-header">
              <div className="bento-icon-wrapper">
                <FaBolt className="bento-icon" />
              </div>
              <span className="bento-badge">USB-C 3.0</span>
            </div>

            <div className="bento-card-body">
              <h3 className="bento-title">Blazing 10Gbps Transfer</h3>
              <p className="bento-text">
                Transfer ProRes 4K video instantly to external SSD drives with USB 3 speeds up to 20x faster than legacy ports.
              </p>

              <div className="usbc-speed-badge">
                <span className="speed-number">10</span>
                <span className="speed-unit">Gbps Data Transfer</span>
              </div>
            </div>

            <div className="bento-glow-ambient usbc-glow" />
          </div>

        </div>
      </div>
    </section>
  );
};

export default BentoGrid;
